import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import Config from '../../../utils/config'
import { UserGuideContext } from './UserGuideContext'

const userGuidePages = {
  // Startpage
  index: 'index.html',
  contents: 'contents.html',

  // WebEDI general
  general: 'contents-general.html',
  login: 'login.html',
  processDescription: 'process-description.html',
  structure: 'structure-of-webedi.html',

  // WebEDI for purchaser
  purchaser: 'contents-purchaser.html',
  suppliers: 'suppliers.html',
  roleAssignments: 'role-assignments.html',
  masterData: 'master-data-management.html',
  purchaserSettings: 'settings-purchaser.html',

  // WebEDI for supplier
  suppliersContent: 'contents-suppliers.html',
  orders: 'order.html',
  ordersOrder: 'orders-order.html',
  despatchAdvice: 'orders-despatch-advice.html',
  invoice: 'orders-invoice.html',
  ordersHistory: 'orders-history.html',
  deliveryForecasts: 'delivery-forecasts.html',
  deliveryForecastsDelivery: 'delivery-forecasts-delivery-forecast.html',
  despatchAdviceDeliveryForecasts: 'delivery-forecasts-despatch-advice.html',
  deliveryForecastsHistory: 'delivery-forecasts-history.html',
  members: 'members.html',
  settings: 'settings.html',
  demoFunctions: 'demo-functions.html',

  //  E-invoice
  eInvoices: 'e-invoices.html',
  eInvoiceValidation: 'e-invoice-validation.html',
  eInvoiceAdditionalData: 'e-invoice-additional-data.html',
  eInvoiceAdditionalDocuments: 'e-invoice-additional-documents.html',
  eInvoiceVerification: 'e-invoice-verification.html',
  eInvoiceSummary: 'e-invoice-summary.html',
  eInvoicesHistory: 'e-invoice-history.html',

  // Not used
  userDocumentation: 'user-documentation.html',
  targetGroup: 'target-group.html',
}

const urlPagePatterns = [
  // Seller >>>>>
  // Orders
  { pattern: /^orders$/, page: userGuidePages.orders },
  { pattern: /^orders\/order\//, page: userGuidePages.ordersOrder },
  {
    pattern: /^orders\/despatch-advice\//,
    page: userGuidePages.despatchAdvice,
  },
  { pattern: /^orders\/invoice\//, page: userGuidePages.invoice },
  { pattern: /^orders\/history/, page: userGuidePages.ordersHistory },

  // Delivery Forecasts
  { pattern: /^delivery-forecasts$/, page: userGuidePages.deliveryForecasts },
  {
    pattern: /^delivery-forecasts\/delivery-forecast\//,
    page: userGuidePages.deliveryForecastsDelivery,
  },
  {
    pattern: /^delivery-forecasts\/despatch-advice\//,
    page: userGuidePages.despatchAdviceDeliveryForecasts,
  },
  {
    pattern: /^delivery-forecasts\/history/,
    page: userGuidePages.deliveryForecastsHistory,
  },

  // Settings & Members
  { pattern: /^settings$/, page: userGuidePages.settings },
  { pattern: /^members$/, page: userGuidePages.members },

  // E-Invoices
  {
    pattern: /^e-invoices\/[^/]+\/validation$/,
    page: userGuidePages.eInvoiceValidation,
  },
  {
    pattern: /^e-invoices\/[^/]+\/additional-data$/,
    page: userGuidePages.eInvoiceAdditionalData,
  },
  {
    pattern: /^e-invoices\/[^/]+\/additional-documents$/,
    page: userGuidePages.eInvoiceAdditionalDocuments,
  },
  {
    pattern: /^e-invoices\/[^/]+\/verification$/,
    page: userGuidePages.eInvoiceVerification,
  },
  {
    pattern: /^e-invoices\/[^/]+\/summary$/,
    page: userGuidePages.eInvoiceSummary,
  },
  { pattern: /^e-invoices$/, page: userGuidePages.eInvoices },
  { pattern: /^e-invoices\/history/, page: userGuidePages.eInvoicesHistory },

  // Demo Functions
  { pattern: /^demo-functions$/, page: userGuidePages.demoFunctions },

  // Purchaser >>>>>
  // { pattern: /^e-invoice-cockpit$/, page: userGuidePages }, // No user guide page for this yet
  // Suppliers
  { pattern: /^suppliers$/, page: userGuidePages.suppliers },
  { pattern: /^suppliers\//, page: userGuidePages.suppliers },
  // Role Assignments
  { pattern: /^role-assignments$/, page: userGuidePages.roleAssignments },
  // Master Data Management (all subpaths)
  { pattern: /^master-data-management/, page: userGuidePages.masterData },
  { pattern: /^buyer-settings$/, page: userGuidePages.purchaserSettings },
]

interface Props {
  children: React.ReactNode
}

const UserGuideContextProvider: React.FC<Props> = ({ children }) => {
  const [isUserGuideOpen, setIsUserGuideOpen] = useState(false)
  const [userGuidePage, setUserGuidePage] = useState(userGuidePages.index)
  const { i18n } = useTranslation()

  const location = useLocation()

  const toggleUserGuide = () => {
    setIsUserGuideOpen(prevValue => !prevValue)
  }
  const closeUserGuide = () => {
    setIsUserGuideOpen(false)
  }

  const updateCurrentPage = () => {
    const url = location.pathname.replace(/^\//, '')

    const foundPage = urlPagePatterns.find(page => page.pattern.test(url))

    if (foundPage?.page || userGuidePages.index !== userGuidePage) {
      setUserGuidePage(foundPage?.page || userGuidePages.index)
    }
  }

  useEffect(() => {
    updateCurrentPage()
  }, [location.pathname])

  const detectedLanguage = i18n.language
  const userGuideUrl = `${Config.userguide.baseUrl}${detectedLanguage}/${userGuidePage}`

  const contextValue = {
    isUserGuideOpen,
    toggleUserGuide,
    closeUserGuide,
    userGuideUrl,
  }

  return (
    <UserGuideContext.Provider value={contextValue}>
      <>{children}</>
    </UserGuideContext.Provider>
  )
}

export default UserGuideContextProvider
